import apiService from '@sonifi/wb-core/services/api.service';
import { ENDPOINTS } from '@sonifi/wb-core/services/endpointsConstants';
import {
  IRD_OPTIONS_TREE_KEYS,
  checkUnpublishedChanges,
  cleanAssignedItems,
  cleanAssignedOptionSets,
  setIrdItems,
  setIrdMenu,
  setIrdOptionSets,
  setIrdShowModal,
  setLoadingItems,
  setNodePreviewSelected,
  setOptionSetDependencies,
  setSavingForm,
} from '../../store/menu/irdMenuSlice';
import { parseQueryParams } from '@sonifi/wb-core/utils/parseHelper';
import { getBodyIrdMenu } from './helpers/irdMenuRequestsHelper';
import {
  addNodeToWbTree,
  buildWbTreeByNodeUri,
  findNodeByUri,
} from '@sonifi/wb-tree/utils/helper';
import { uniqueId } from 'lodash';
import {
  getIrdOptionsSetsDependencies,
  getOptionSetsDependencies,
} from './irdDependencies.service';
import { setAlert } from '@sonifi/wb-notifier/store/notifierSlice';
import { ALERTS_IDS } from '@sonifi/wb-notifier/constants/notifierConstants';
import { IRD_NODE_TYPES } from '@sonifi/wb-in-room-dining/constants/wbInRoomDiningConstants';

export const getIrdOptionSets =
  ({
    page = 0,
    revenueCenterId,
    query = '',
    orderBy = 'title',
    price,
    rpp = 30,
    optionTreeKey,
  }) =>
  async (dispatch, getState) => {
    try {
      dispatch(setLoadingItems({ optionTreeKey, loading: true }));
      const {
        groups: { selectedGroup },
      } = getState();
      const params = parseQueryParams({
        _order_by: orderBy,
        _query: query,
        group: selectedGroup.uri,
        page,
        revenue_center_id: revenueCenterId,
        rpp,
        price,
      });

      const url = `${ENDPOINTS.IRD_OPTION_SETS}?${params}`;
      const res = await apiService.get(url);
      const { total, objects, page: resultsPage, _embedded } = res.data;
      const irdOptionSets = objects.map((item) => ({
        id: item.id,
        isVisible: true,
        secondaryValue: item.price
          ? new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(item.price / 100)
          : '',
        subject: item,
        type: 'ird_options',
      }));

      const itemsDependencies = Object.values(_embedded);

      await dispatch(
        setIrdOptionSets({
          itemsDependencies,
          optionSets: irdOptionSets,
          orderBy,
          page: resultsPage,
          query,
          rpp,
          total,
          totalPages: Math.ceil(total / rpp),
          optionTreeKey,
        }),
      );
      dispatch(setLoadingItems({ optionTreeKey, loading: false }));
      return irdOptionSets;
    } catch (error) {
      dispatch(setLoadingItems({ optionTreeKey, loading: false }));
      return null;
    }
  };

export const getIrdItems =
  ({
    page = 1,
    revenueCenterId,
    query = '',
    orderBy = 'title',
    price,
    optionTreeKey,
    rpp = 30,
  }) =>
  async (dispatch, getState) => {
    try {
      dispatch(setLoadingItems({ optionTreeKey, loading: true }));
      const {
        groups: { selectedGroup },
        irdMenu: { optionSetsDependencies },
      } = getState();

      const params = parseQueryParams({
        _order_by: orderBy,
        _query: query,
        group: selectedGroup.uri,
        page,
        revenue_center_id: revenueCenterId,
        rpp,
        price,
      });

      const url = `${ENDPOINTS.IRD_ITEMS}?${params}`;
      const res = await apiService.get(url);
      const { total, objects, page: resultsPage } = res.data;
      const irdItems = objects.map((item) => ({
        id: item.id,
        secondaryValue: item.price
          ? new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(item.price / 100)
          : '',
        subject: item,
        isVisible: true,
        type: IRD_NODE_TYPES.ird_items,
        indicatorImage: {
          showIcon: item.ird_option_sets?.length > 0,
          showTooltip: item.ird_option_sets?.length > 0,
        },
      }));

      const optionSets = getIrdOptionsSetsDependencies({
        irdItems,
        currentOptionSetsDependencies: optionSetsDependencies,
      });
      const optionSetsDependenciesLeft = await getOptionSetsDependencies(
        optionSets,
      );

      await Promise.all([
        dispatch(
          setOptionSetDependencies({
            optionSetsDependencies: [
              ...optionSetsDependencies,
              ...optionSetsDependenciesLeft,
            ],
          }),
        ),
        dispatch(
          setIrdItems({
            page: resultsPage,
            total,
            rpp,
            items: irdItems,
            orderBy,
            query,
            totalPages: Math.ceil(total / rpp),
            optionTreeKey,
          }),
        ),
        dispatch(setLoadingItems({ optionTreeKey, loading: false })),
      ]);

      return irdItems;
    } catch (error) {
      dispatch(setLoadingItems({ optionTreeKey, loading: false }));
      return null;
    }
  };

export const createOptionSet =
  ({ title }) =>
  async (dispatch, getState) => {
    try {
      const {
        groups: { selectedGroup },
        irdMenu: { items },
        irdSettings: { revenueCenterSelected },
      } = getState();
      const optionSetBody = {
        title,
        group: selectedGroup.uri,
        ird_items: [],
        revenue_center: revenueCenterSelected.uri,
        description: 'Please select from the following options:',
      };

      await apiService.post(ENDPOINTS.IRD_OPTION_SETS, optionSetBody);
      dispatch(
        getIrdOptionSets({
          revenueCenterId: revenueCenterSelected.id,
          optionTreeKey: IRD_OPTIONS_TREE_KEYS.ITEM_MODAL,
          ...items[IRD_OPTIONS_TREE_KEYS.ITEM_MODAL],
        }),
      );
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

export const createItem =
  ({ price, title }) =>
  async (dispatch, getState) => {
    try {
      const {
        groups: { selectedGroup },
        irdMenu: { items },
        irdSettings: { revenueCenterSelected },
      } = getState();
      const itemBody = {
        group: selectedGroup.uri,
        image: [],
        ird_option_sets: [],
        price: price && price !== '0.0' ? price : undefined,
        revenue_center: revenueCenterSelected.uri,
        title,
      };
      await apiService.post(ENDPOINTS.IRD_ITEMS, itemBody);
      dispatch(
        getIrdItems({
          revenueCenterId: revenueCenterSelected.id,
          optionTreeKey: IRD_OPTIONS_TREE_KEYS.OPTION_SET_MODAL,
          ...items[IRD_OPTIONS_TREE_KEYS.OPTION_SET_MODAL],
        }),
      );
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

export const submitItemForm =
  ({ assignedOptionSets, form, keepModalOpen, width, height, aspectRatio }) =>
  async (dispatch, getState) => {
    dispatch(setSavingForm(true));
    try {
      const {
        groups: { selectedGroup },
        irdMenu: { irdMenu, items, addNodeFrom, nodePreviewSelected },
        irdSettings: { revenueCenterSelected },
      } = getState();

      const { description, id, image, price, sku, title, uri } = form.subject;

      const ird_option_sets = assignedOptionSets.map((option) => option.uri);

      const itemsBody = {
        description,
        group: selectedGroup.uri,
        id,
        image: [],
        ird_option_sets,
        revenue_center: revenueCenterSelected.uri,
        title,
        uri,
      };

      if (price) {
        itemsBody.price = price;
      }

      if (sku) {
        itemsBody.sku = sku;
      }

      if (image?.length && image[0].file_uri) {
        const lastSlashIndex = image[0].file_uri.lastIndexOf('.');
        const format =
          image[0]?.file_uri
            ?.substring(lastSlashIndex + 1)
            .replace('jpeg', 'jpg') || 'jpg';
        const thumbnailBody = {
          flip: image[0]?.transform.flip,
          format,
          image_url: image[0]?.file_uri,
          position: {
            anchor: [image[0]?.x, image[0]?.y],
            height,
            width,
          },
          scale: image[0]?.transform?.scale,
        };
        const thumbnailRes = await apiService.post(
          ENDPOINTS.THUMBNAILS,
          thumbnailBody,
        );

        image[0].display_uri = thumbnailRes.data.signed_url;
        image[0].aspect_ratio = aspectRatio;
        const imageCopy = { ...image[0] };
        delete imageCopy.file_uri;
        delete imageCopy._embedded;
        itemsBody.image = [imageCopy];
      }

      let irdMenuUpdated = irdMenu;

      if (form.id) {
        const res = await apiService.patch(
          `${ENDPOINTS.IRD_ITEMS}/${id}`,
          itemsBody,
        );
        const currentTreeNode = findNodeByUri({
          menu: irdMenu.menu,
          nodeUri: form.subject.uri,
        });

        if (currentTreeNode) {
          const node = {
            ...currentTreeNode,
            subject: {
              ...currentTreeNode.subject,
              ...res.data,
              image: image?.length ? image : [],
            },
            indicatorImage: {
              showIcon: assignedOptionSets?.length > 0,
              showTooltip: assignedOptionSets?.length > 0,
            },
          };
          irdMenuUpdated = buildWbTreeByNodeUri({
            wbTree: irdMenu,
            node,
          });
        }
      } else {
        const res = await apiService.post(ENDPOINTS.IRD_ITEMS, itemsBody);

        if (addNodeFrom) {
          const node = {
            id: uniqueId(`${Date.now()}_`),
            active: true,
            subject: {
              ...form.subject,
              ...res.data,
              hasDeletedContent: false,
            },
            indicatorImage: {
              showIcon: res.data.ird_option_sets?.length > 0,
              showTooltip: res.data.ird_option_sets?.length > 0,
            },
            secondaryValue: '',
            type: IRD_NODE_TYPES.ird_items,
          };
          irdMenuUpdated = addNodeToWbTree({
            addNodeFrom,
            node,
            wbTree: irdMenu,
          });
        }
      }
      const irdMenuBody = getBodyIrdMenu({ irdMenu: irdMenuUpdated });
      const resMenu = await apiService.patch(
        `${ENDPOINTS.IRD_MENUS}/${irdMenu.id}`,
        irdMenuBody,
      );
      const nodePreviewUpdated = findNodeByUri({
        menu: irdMenuUpdated.menu,
        nodeUri: nodePreviewSelected.subject.uri,
      });

      await Promise.all([
        dispatch(
          setIrdMenu({
            irdMenu: {
              ...irdMenuUpdated,
              last_updated: resMenu.data.last_updated,
            },
          }),
        ),
        dispatch(
          getIrdItems({
            revenueCenterId: revenueCenterSelected.id,
            optionTreeKey: IRD_OPTIONS_TREE_KEYS.MENU_BUILDER,
            ...items[IRD_OPTIONS_TREE_KEYS.MENU_BUILDER],
          }),
        ),
        dispatch(setNodePreviewSelected({ node: nodePreviewUpdated })),
        dispatch(
          cleanAssignedOptionSets({
            optionTreeKey: IRD_OPTIONS_TREE_KEYS.ITEM_MODAL,
          }),
        ),
        !keepModalOpen && dispatch(setIrdShowModal(false)),
        dispatch(checkUnpublishedChanges()),
        dispatch(setSavingForm(false)),
      ]);
      return true;
    } catch (error) {
      console.error(error);
      dispatch(setSavingForm(false));
      if (error.length) {
        let msg =
          'Unable to save this node. If this error persists, please contact support.';

        for (const err of error) {
          if (err.alternate_error) {
            msg += '<br/>' + err.alternate_error;
          } else if (err.error) {
            msg += '<br/>' + err.error;
          }
        }
        dispatch(
          setAlert({
            id: ALERTS_IDS.irdModal,
            content: msg,
            type: 'danger',
            show: true,
            dismissable: true,
          }),
        );
      }
      return Promise.reject(error);
    }
  };

export const submitOptionSetForm =
  ({ assignedItems, form, keepModalOpen }) =>
  async (dispatch, getState) => {
    dispatch(setSavingForm(true));
    try {
      const {
        groups: { selectedGroup },
        irdMenu: { items },
        irdSettings: { revenueCenterSelected },
      } = getState();
      const {
        subject: {
          id,
          description,
          group,
          maximum,
          minimum,
          revenue_center,
          title,
          uri,
        },
      } = form;

      const ird_items = assignedItems.map((option) => option.uri);

      let optionSetBody = {
        id,
        description,
        group,
        ird_items,
        maximum,
        minimum,
        revenue_center,
        title,
        uri,
      };

      if (!id) {
        optionSetBody = {
          description,
          group: selectedGroup.uri,
          ird_items,
          maximum,
          minimum,
          revenue_center: revenueCenterSelected.uri,
          title,
        };
        await apiService.post(ENDPOINTS.IRD_OPTION_SETS, optionSetBody);
      } else {
        optionSetBody = {
          id,
          description,
          group,
          ird_items,
          maximum,
          minimum,
          revenue_center: revenueCenterSelected.uri,
          title,
          uri,
        };
        await apiService.patch(
          `${ENDPOINTS.IRD_OPTION_SETS}/${id}`,
          optionSetBody,
        );
      }

      await Promise.all([
        dispatch(
          getIrdOptionSets({
            revenueCenterId: revenueCenterSelected.id,
            optionTreeKey: IRD_OPTIONS_TREE_KEYS.MENU_BUILDER,
            ...items[IRD_OPTIONS_TREE_KEYS.MENU_BUILDER],
          }),
        ),
        dispatch(
          cleanAssignedItems({
            optionTreeKey: IRD_OPTIONS_TREE_KEYS.OPTION_SET_MODAL,
          }),
        ),
        !keepModalOpen && dispatch(setIrdShowModal(false)),
        dispatch(setSavingForm(false)),
      ]);
    } catch (error) {
      console.error(error);
      dispatch(setSavingForm(false));
      if (error.length) {
        let msg =
          'Unable to save this node. If this error persists, please contact support.';

        for (const err of error) {
          if (err.alternate_error) {
            msg += '<br/>' + err.alternate_error;
          } else if (err.error) {
            msg += '<br/>' + err.error;
          }
        }
        dispatch(
          setAlert({
            id: ALERTS_IDS.irdModal,
            content: msg,
            type: 'danger',
            show: true,
            dismissable: true,
          }),
        );
      }
    }
  };
